import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllLevels,
  getTrainingData,
  getAllCoachesData,
  updateTraining,
  addNewTraining,
  getAllMasterclassesApi,
  createMasterclassApi,
  updateMasterclassApi,
  deleteMasterclassApi,
  updateCommunityLinkForMasterclassApi,
  updateMasterclassTrainingApi,
  deleteMasterclassTrainingApi,
  getAllMasterclassTrainingsApi,
  createMasterclassTrainingApi,
  getAllSharedMasterclassTrainingsApi
} from "../../api/trainingApi";

// Async thunk for adding a new meal
export const addNewTrainings = createAsyncThunk(
  "mod/addNewTrainings",
  async (data, thunkAPI) => {
    try {
      const response = await addNewTraining(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateTrainings = createAsyncThunk(
  "mod/updateTrainings",
  async ({ trainingData, levelId, trainingId }, thunkAPI) => {
    try {
      const response = await updateTraining(trainingData, levelId, trainingId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAllTrainings = createAsyncThunk(
  "mod/getAllTrainings",
  async ({ page = 0, limit = 10, search, level }, thunkAPI) => {
    try {
      const response = await getTrainingData(page, limit, search, level);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAllCoaches = createAsyncThunk(
  "mod/getAllCoaches",
  async (_, thunkAPI) => {
    try {
      const response = await getAllCoachesData();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAllLevelDetails = createAsyncThunk(
  "mod/getAllLevelDetails",
  async (userId, thunkAPI) => {
    try {
      const response = await getAllLevels(userId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createMasterclass = createAsyncThunk(
  "training/createMasterclass",
  async (masterclassData, thunkAPI) => {
    try {
      return await createMasterclassApi(masterclassData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAllMasterclasses = createAsyncThunk(
  "training/getAllMasterclasses",
  async (params = {}, thunkAPI) => {
    try {
      return await getAllMasterclassesApi(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateMasterclass = createAsyncThunk(
  "training/updateMasterclass",
  async ({ id, ...updateData }, thunkAPI) => {
    try {
      return await updateMasterclassApi(id, updateData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteMasterclass = createAsyncThunk(
  "training/deleteMasterclass",
  async (id, thunkAPI) => {
    try {
      await deleteMasterclassApi(id);
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateMasterclassCommunityLink = createAsyncThunk(
  "training/updateMasterclassCommunityLink",
  async ({ id, level, link }, thunkAPI) => {
    try {
      return await updateCommunityLinkForMasterclassApi(id, level, link);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createMasterclassTraining = createAsyncThunk(
  "training/createMasterclassTraining",
  async (trainingData, thunkAPI) => {
    try {
      return await createMasterclassTrainingApi(trainingData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAllMasterclassTrainings = createAsyncThunk(
  "training/getAllMasterclassTrainings",
  async (params, thunkAPI) => {
    try {
      return await getAllMasterclassTrainingsApi(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const getAllSharedMasterclassTrainings = createAsyncThunk(
  "training/getAllSharedMasterclassTrainings",
  async (params, thunkAPI) => {
    try {
      return await getAllSharedMasterclassTrainingsApi(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateMasterclassTraining = createAsyncThunk(
  "training/updateMasterclassTraining",
  async ({ id, ...updateData }, thunkAPI) => {
    try {
      return await updateMasterclassTrainingApi(id, updateData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteMasterclassTraining = createAsyncThunk(
  "training/deleteMasterclassTraining",
  async (id, thunkAPI) => {
    try {
      await deleteMasterclassTrainingApi(id);
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Initial state
const initialState = {
  trainings: [], // holds the meals data
  loading: false, // loading state for getAllMeals
  error: null, // error state
  total: 0,
  page: 0,
  availableCoaches: [],
  levelData: [],
  limit: 10,
  masterclasses: [],
  masterclassLoading: false,
  masterclassError: null,
  selectedMasterclass: null,
  masterclassTrainings: [],
  masterclassTrainingLoading: false,
  masterclassTrainingError: null,
  totalMasterclassTrainings: 0
};

const traininglice = createSlice({
  name: "training",
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSelectedMasterclass: (state, action) => {
      state.selectedMasterclass = action.payload;
    },
    clearMasterclassError: (state) => {
      state.masterclassError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handling getAllMeals actions
      .addCase(getAllTrainings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTrainings.fulfilled, (state, action) => {
        state.loading = false;
        state.trainings = action.payload.trainings; // assuming response has meals data
        state.total = action.payload.totalTrainings; // total pages
      })
      .addCase(getAllTrainings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllCoaches.pending, (state) => {
        state.availableCoaches = [];
      })
      .addCase(getAllCoaches.fulfilled, (state, action) => {
        state.loading = false;
        state.availableCoaches = action.payload.users;
      })
      .addCase(getAllCoaches.rejected, (state, action) => {
        state.availableCoaches = [];
        state.error = action.payload;
      })
      .addCase(getAllLevelDetails.pending, (state) => {
        state.levelData = [];
      })
      .addCase(getAllLevelDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.levelData = action.payload.levels;
      })
      .addCase(getAllLevelDetails.rejected, (state, action) => {
        state.levelData = [];
        state.error = action.payload;
      })
      .addCase(createMasterclass.pending, (state) => {
        state.masterclassLoading = true;
        state.masterclassError = null;
      })
      .addCase(createMasterclass.fulfilled, (state, action) => {
        state.masterclassLoading = false;
        state.masterclasses.push(action.payload.masterclass);
      })
      .addCase(createMasterclass.rejected, (state, action) => {
        state.masterclassLoading = false;
        state.masterclassError = action.payload;
      })

      // Get All Masterclasses
      .addCase(getAllMasterclasses.pending, (state) => {
        state.masterclassLoading = true;
        state.masterclassError = null;
      })
      .addCase(getAllMasterclasses.fulfilled, (state, action) => {
        state.masterclassLoading = false;
        state.masterclasses = action.payload.masterclasses;
        state.totalMasterclasses = action.payload.total;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(getAllMasterclasses.rejected, (state, action) => {
        state.masterclassLoading = false;
        state.masterclassError = action.payload;
      })

      // Update Masterclass
      .addCase(updateMasterclass.pending, (state) => {
        state.masterclassLoading = true;
        state.masterclassError = null;
      })
      .addCase(updateMasterclass.fulfilled, (state, action) => {
        state.masterclassLoading = false;
        const index = state.masterclasses.findIndex(
          (mc) => mc._id === action.payload.masterclass._id
        );
        if (index !== -1) {
          state.masterclasses[index] = action.payload.masterclass;
        }
      })
      .addCase(updateMasterclass.rejected, (state, action) => {
        state.masterclassLoading = false;
        state.masterclassError = action.payload;
      })

      // Delete Masterclass
      .addCase(deleteMasterclass.pending, (state) => {
        state.masterclassLoading = true;
        state.masterclassError = null;
      })
      .addCase(deleteMasterclass.fulfilled, (state, action) => {
        state.masterclassLoading = false;
        state.masterclasses = state.masterclasses.filter(
          (mc) => mc._id !== action.payload.id
        );
      })
      .addCase(deleteMasterclass.rejected, (state, action) => {
        state.masterclassLoading = false;
        state.masterclassError = action.payload;
      })

      // Update Community Link
      .addCase(updateMasterclassCommunityLink.pending, (state) => {
        state.masterclassLoading = true;
        state.masterclassError = null;
      })
      .addCase(updateMasterclassCommunityLink.fulfilled, (state, action) => {
        state.masterclassLoading = false;
        const index = state.masterclasses.findIndex(
          (mc) => mc._id === action.payload.masterclass._id
        );
        if (index !== -1) {
          state.masterclasses[index] = action.payload.masterclass;
        }
      })
      .addCase(updateMasterclassCommunityLink.rejected, (state, action) => {
        state.masterclassLoading = false;
        state.masterclassError = action.payload;
      })
      .addCase(createMasterclassTraining.pending, (state) => {
        state.masterclassTrainingLoading = true;
        state.masterclassTrainingError = null;
      })
      .addCase(createMasterclassTraining.fulfilled, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainings.push(action.payload.training);
      })
      .addCase(createMasterclassTraining.rejected, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainingError = action.payload;
      })

      // Get All Masterclass Trainings
      .addCase(getAllMasterclassTrainings.pending, (state) => {
        state.masterclassTrainingLoading = true;
        state.masterclassTrainingError = null;
      })
      .addCase(getAllMasterclassTrainings.fulfilled, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainings = action.payload.trainings;
        state.totalMasterclassTrainings = action.payload.total;
      })
      .addCase(getAllMasterclassTrainings.rejected, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainingError = action.payload;
      })
      .addCase(getAllSharedMasterclassTrainings.pending, (state) => {
        state.masterclassTrainingLoading = true;
        state.masterclassTrainingError = null;
      })
      .addCase(getAllSharedMasterclassTrainings.fulfilled, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainings = action.payload.trainings;
        state.totalMasterclassTrainings = action.payload.total;
      })
      .addCase(getAllSharedMasterclassTrainings.rejected, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainingError = action.payload;
      })

      // Update Masterclass Training
      .addCase(updateMasterclassTraining.pending, (state) => {
        state.masterclassTrainingLoading = true;
        state.masterclassTrainingError = null;
      })
      .addCase(updateMasterclassTraining.fulfilled, (state, action) => {
        state.masterclassTrainingLoading = false;
        const index = state.masterclassTrainings.findIndex(
          (training) => training._id === action.payload.training._id
        );
        if (index !== -1) {
          state.masterclassTrainings[index] = action.payload.training;
        }
      })
      .addCase(updateMasterclassTraining.rejected, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainingError = action.payload;
      })

      // Delete Masterclass Training
      .addCase(deleteMasterclassTraining.pending, (state) => {
        state.masterclassTrainingLoading = true;
        state.masterclassTrainingError = null;
      })
      .addCase(deleteMasterclassTraining.fulfilled, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainings = state.masterclassTrainings.filter(
          (training) => training._id !== action.payload.id
        );
      })
      .addCase(deleteMasterclassTraining.rejected, (state, action) => {
        state.masterclassTrainingLoading = false;
        state.masterclassTrainingError = action.payload;
      });
  }
});

export const {
  setPage,
  setLimit,
  setSelectedMasterclass,
  clearMasterclassError
} = traininglice.actions;

export default traininglice.reducer;
